import { toast } from '@buffer-mono/popcorn'

import { graphql } from '~publish/graphql'
import { useTypedMutation } from '~publish/hooks/useTypedMutation'

export const ForceCommentsBackfillMutationGQL = graphql(/* GraphQL */ `
  mutation ForceCommentsBackfillForChannel(
    $input: ForceCommentsBackfillForChannelInput!
  ) {
    forceCommentsBackfillForChannel(input: $input) {
      __typename
      ... on ForceCommentsBackfillForChannelSuccess {
        channelIds
      }
      ... on LimitReachedError {
        message
      }
      ... on VoidMutationError {
        message
      }
    }
  }
`)

type ForceBackfillInput = {
  channelIds: string[]
  organizationId: string
  userForced?: boolean
}

type ForceBackfillResult = {
  success: boolean
  channelIds: string[]
}

type UseForceCommentsBackfillResult = {
  forceBackfill: ({
    channelIds,
    organizationId,
    userForced,
  }: ForceBackfillInput) => Promise<ForceBackfillResult>
  loading: boolean
}

export const useForceCommentsBackfill = (): UseForceCommentsBackfillResult => {
  const [mutate, { loading }] = useTypedMutation(
    ForceCommentsBackfillMutationGQL,
    (data) => data.forceCommentsBackfillForChannel,
    {
      successTypename: 'ForceCommentsBackfillForChannelSuccess',
    },
  )

  const forceBackfill = async ({
    channelIds,
    organizationId,
    userForced = false,
  }: ForceBackfillInput): Promise<ForceBackfillResult> => {
    try {
      const result = await mutate({
        variables: {
          input: {
            organizationId,
            channelIds,
            userForced,
          },
        },
      })

      if (!result.success) {
        toast.error(
          result.error?.message ?? 'An error occurred while syncing comments',
          {
            duration: 3_000,
          },
        )
        return { success: false, channelIds: [] }
      }

      return {
        success: true,
        channelIds: result.data.channelIds,
      }
    } catch (error) {
      toast.error('An error occurred while syncing comments')
      // eslint-disable-next-line no-console
      console.error('Error forcing comments backfill:', error)
      return { success: false, channelIds: [] }
    }
  }

  return {
    forceBackfill,
    loading,
  }
}
